<template>
  <q-dialog
    ref="dialogRef"
    persistent
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin"
      style="width: 500px; max-width: 95%"
    >
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <q-icon
            name="credit_card"
            class="q-mr-sm"
          />
          Enter Card Number
        </div>
        <q-space />
        <q-btn
          icon="close"
          flat
          round
          dense
          @click="onDialogCancel"
        />
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="text-caption text-grey-6 q-mb-md">
          Enter a card number or QR code
        </div>

        <!-- Card Input -->
        <q-input
          v-model="uid"
          label="Card Number / QR Code"
          outlined
          dense
          :rules="[
            val => !!val || 'Card number is required',
            val => val.length >= 3 || 'Please enter a valid card number'
          ]"
          @keyup.enter="onOKClick(uid, pin)"
        >
          <template #prepend>
            <q-icon name="search" />
          </template>
        </q-input>

        <!-- PIN Input (when required) -->
        <q-input
          v-if="cardPinRequired"
          v-model="pin"
          label="Card PIN"
          type="password"
          outlined
          dense
          :rules="[
            val => !!val || 'PIN is required',
            val => val.length >= 3 || 'PIN must be at least 3 digits',
            val => /^\d+$/.test(val) || 'PIN must contain only numbers'
          ]"
          class="q-mt-md"
        >
          <template #prepend>
            <q-icon name="vpn_key" />
          </template>
        </q-input>
      </q-card-section>

      <q-card-actions class="q-pa-md">
        <q-btn
          flat
          label="Cancel"
          @click="onDialogCancel"
        />
        <q-space />
        <q-btn
          color="primary"
          :label="buttonLabel"
          :disabled="!isFormValid"
          @click="onOKClick(uid, pin)"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { useDialogPluginComponent } from 'quasar'
import { useCardPinRequired } from 'src/modules/register/composables/card/pin-required'

export default {
  name: 'CardSearchDialog',

  props: {
    buttonLabel: {
      type: String,
      default: 'Search'
    }
  },

  emits: [
    // REQUIRED; need to specify some events that your
    // component will emit through useDialogPluginComponent()
    ...useDialogPluginComponent.emits
  ],

  setup (props) {
    // REQUIRED; must be called inside of setup()
    const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()
    // dialogRef      - Vue ref to be applied to QDialog
    // onDialogHide   - Function to be used as handler for @hide on QDialog
    // onDialogOK     - Function to call to settle dialog with "ok" outcome
    //                    example: onDialogOK() - no payload
    //                    example: onDialogOK({ /*.../* }) - with payload
    // onDialogCancel - Function to call to settle dialog with "cancel" outcome

    const { cardPinRequired, uid } = useCardPinRequired()

    return {
      // This is REQUIRED;
      // Need to inject these (from useDialogPluginComponent() call)
      // into the vue scope for the vue html template
      dialogRef,
      onDialogHide,
      onDialogCancel,
      cardPinRequired,
      uid,
      // other methods that we used in our vue html template;
      // these are part of our example (so not required)
      onOKClick (uid, pin) {
        // on OK, it is REQUIRED to
        // call onDialogOK (with optional payload)
        // Always return UID and PIN (if required)
        if (this.cardPinRequired) {
          onDialogOK({ uid, pin })
        } else {
          onDialogOK({ uid })
        }
        // ...and it will also hide the dialog automatically
      }
    }
  },

  data () {
    return {
      pin: null
    }
  },

  computed: {
    isFormValid () {
      // Check UID validation
      if (!this.uid || this.uid.length < 3) {
        return false
      }

      // Check PIN validation if required
      if (this.cardPinRequired) {
        if (!this.pin) {
          return false
        }
        if (this.pin.length < 3) {
          return false
        }
        if (!/^\d+$/.test(this.pin)) {
          return false
        }
      }

      return true
    }
  },

  configuration: ['currentSite'],

  methods: {
    resetDialog () {
      this.uid = null
      this.pin = null
    }
  }
}
</script>

<style scoped>
.q-dialog-plugin {
  border-radius: 8px;
}
</style>
